import { createApp, h } from 'vue';
import DDialog from '@components/DDialog.vue';

/**
 * Programmatically creates and mounts a DDialog confirmation dialog.
 * Returns a Promise that resolves when the user confirms, or rejects when
 * the user cancels. An optional `onConfirm` callback in `options` is also
 * called on confirmation for callers that prefer the callback style.
 *
 * @param {Object} options - DDialog props (title, message, confirmText, confirmColor, etc.)
 * @returns {Promise<any>} Resolves with the prompt value on confirm; rejects on cancel.
 */
const createConfirmDialog = (options) => {
  return new Promise((resolve, reject) => {
    if (typeof document === 'undefined') {
      reject(new Error('Document not available'));
      return;
    }

    const container = document.createElement('div');
    document.body.appendChild(container);

    let dialogApp = null;
    let settled = false;

    const cleanup = () => {
      // Delay teardown so DDialog's close animation can finish
      setTimeout(() => {
        try { dialogApp && dialogApp.unmount(); } catch (e) { /* ignore unmount errors during cleanup */ }
        try { container.parentNode && container.parentNode.removeChild(container); } catch (e) { /* ignore DOM removal errors during cleanup */ }
      }, 200);
    };

    const handleConfirm = (promptValue) => {
      if (settled) return;
      settled = true;
      cleanup();
      if (typeof options.onConfirm === 'function') {
        options.onConfirm(promptValue);
      }
      resolve(promptValue);
    };

    const handleCancel = () => {
      if (settled) return;
      settled = true;
      cleanup();
      reject(new Error('Dialog cancelled'));
    };

    dialogApp = createApp({
      render() {
        return h(DDialog, {
          title: options.title,
          message: options.message,
          confirmText: options.confirmText,
          confirmColor: options.confirmColor,
          cancelText: options.cancelText,
          hasInput: options.hasInput || false,
          inputAttrs: options.inputAttrs,
          optionalInput: options.optionalInput || false,
          programmatic: true,
          onConfirm: handleConfirm,
          onCancel: handleCancel,
        });
      },
    });

    dialogApp.mount(container);
  });
};

export const dialog = {
  confirm: (options) => createConfirmDialog(options),
};

export const installDialog = (app) => {
  app.config.globalProperties.$dialog = dialog;
  app.provide('dialog', dialog);
};
